import React, { useState } from 'react';
import { Quote, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { mockData } from '../mockData';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const STAR_KEYS = ['star-1', 'star-2', 'star-3', 'star-4', 'star-5'];

const TestimonialCard = ({ testimonial }) => (
  <Card className="bg-white/95 backdrop-blur-sm border-2 border-gold/20 shadow-2xl">
    <CardContent className="p-12 md:p-16 space-y-8">
      <div className="flex justify-center">
        <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center">
          <Quote className="w-10 h-10 text-gold" />
        </div>
      </div>

      <div className="flex justify-center space-x-1">
        {STAR_KEYS.map((key) => (
          <Star key={key} className="w-6 h-6 fill-gold text-gold" />
        ))}
      </div>

      <blockquote className="text-center">
        <p className="text-xl md:text-2xl text-navy/80 italic leading-relaxed font-serif">
          "{testimonial.text}"
        </p>
      </blockquote>

      <div className="text-center space-y-2 pt-6 border-t border-navy/10">
        <div className="font-bold text-xl text-navy" data-testid="testimonial-author">
          {testimonial.name}
        </div>
        <div className="text-gold font-medium">
          Cliente de {testimonial.lawyer}
        </div>
      </div>
    </CardContent>
  </Card>
);

const CarouselControls = ({ currentIndex, onPrev, onNext, onSelect }) => (
  <div className="flex justify-center items-center space-x-4 mt-8">
    <Button
      onClick={onPrev}
      variant="outline"
      size="icon"
      data-testid="testimonial-prev-button"
      className="w-12 h-12 rounded-full border-2 border-white/30 bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-300"
    >
      <ChevronLeft className="w-6 h-6" />
    </Button>

    <div className="flex space-x-2">
      {mockData.testimonials.map((testimonial, index) => (
        <button
          key={testimonial.id}
          onClick={() => onSelect(index)}
          data-testid={`testimonial-dot-${index}`}
          className={`transition-all duration-300 rounded-full ${
            index === currentIndex
              ? 'w-8 h-3 bg-gold'
              : 'w-3 h-3 bg-white/30 hover:bg-white/50'
          }`}
          aria-label={`Ver testemunho ${index + 1}`}
        />
      ))}
    </div>

    <Button
      onClick={onNext}
      variant="outline"
      size="icon"
      data-testid="testimonial-next-button"
      className="w-12 h-12 rounded-full border-2 border-white/30 bg-white/10 hover:bg-white/20 text-white backdrop-blur-sm transition-all duration-300"
    >
      <ChevronRight className="w-6 h-6" />
    </Button>
  </div>
);

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const total = mockData.testimonials.length;

  const nextTestimonial = () => setCurrentIndex((prev) => (prev + 1) % total);
  const prevTestimonial = () => setCurrentIndex((prev) => (prev - 1 + total) % total);

  return (
    <section id="testimonials" className="py-24 bg-navy relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("${mockData.images.testimonialsPattern}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      ></div>

      <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy/95 to-navy/90"></div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <div className="inline-block">
            <span className="text-gold font-semibold text-sm uppercase tracking-wider">
              Testemunhos
            </span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white leading-tight">
            O Que Dizem os
            <span className="text-gold"> Nossos Clientes</span>
          </h2>
          <p className="text-lg text-white/70 leading-relaxed">
            A confiança e satisfação dos nossos clientes são o reflexo do nosso compromisso com a excelência.
          </p>
        </div>

        <div className="relative">
          <TestimonialCard testimonial={mockData.testimonials[currentIndex]} />
          <CarouselControls
            currentIndex={currentIndex}
            onPrev={prevTestimonial}
            onNext={nextTestimonial}
            onSelect={setCurrentIndex}
          />
          <div className="text-center mt-6">
            <span className="text-white/60 text-sm" data-testid="testimonial-counter">
              {currentIndex + 1} / {total}
            </span>
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-8 py-4 rounded-full border border-white/20">
            <Star className="w-6 h-6 text-gold fill-gold" />
            <span className="text-white font-semibold text-lg">
              Clientes Satisfeitos com o Nosso Trabalho
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
